import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useRefresh } from 'react-admin';
import { useRunRequest } from '@/hooks';
import { TurnResourcesEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { FCRAWorker } from '@/types/turn-api';
import { transformAddressDetails } from '../../AddAddressForm/utils';
import { EditAddressFormProps } from '../types';

const EDITABLE_FIELDS = [
  'address1',
  'address2',
  'city',
  'county',
  'state',
  'zip',
  'zip4',
  'latitude',
  'longitude',
  'first_seen',
  'last_seen',
] as const;

const useController = ({ address, onClose, pendingPlace }: EditAddressFormProps) => {
  const {
    handleSubmit,
    setValue,
    formState: { dirtyFields, isValid },
  } = useFormContext<FCRAWorker.Address>();
  const { runRequest } = useRunRequest();
  const { id: workerId } = useParams<{ id: string }>();
  const refresh = useRefresh();

  const handleEditAddress = async (values: FCRAWorker.Address) => {
    const data: Partial<Record<(typeof EDITABLE_FIELDS)[number], string | number | null>> = {};

    for (const field of EDITABLE_FIELDS) {
      if (dirtyFields[field]) {
        if (field === 'state') {
          data.state = values.state?.replace(/^US-/, '') ?? '';
        } else if (field === 'latitude' || field === 'longitude') {
          const val = values[field];
          data[field] = val != null && val !== '' ? String(val) : null;
        } else {
          data[field] = values[field] ?? null;
        }
      }
    }

    if (Object.keys(data).length === 0) {
      return;
    }

    await runRequest({
      fetchType: 'PATCH',
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id: workerId,
        target: TurnTargetsEnum.Addresses,
        targetId: address.id,
        data,
      },
      mode: 'notify',
      notify: {
        onError: { message: 'Error: Address could not be updated' },
        onSuccess: {
          message: 'Address updated successfully',
          postAction: () => {
            onClose();
            refresh();
          },
        },
      },
    });
  };

  useEffect(() => {
    if (!pendingPlace) return;

    const transformed = transformAddressDetails(pendingPlace);
    Object.entries(transformed).forEach(([field, value]) => {
      setValue(field as keyof FCRAWorker.Address, value, { shouldDirty: true, shouldValidate: true });
    });
  }, [pendingPlace, setValue]);

  return {
    handleEditAddress: handleSubmit(handleEditAddress),
    isDirty: Object.keys(dirtyFields).length > 0,
    isValid,
  };
};

export default useController;
